import { writable, get } from "$utils/shim";
import Knock, { KnockGuideClient } from "@knocklabs/client";

// Headless Knock Guides store, mirroring `notifications.ts` but for the Guides
// API. Unlike notifications we deliberately do NOT open the real-time websocket
// (`guideClient.subscribe()`): onboarding cards are authored ahead of time and
// only need to appear on the right route, so we fetch on mount and again on
// navigation rather than listening for live pushes. This keeps a second browser
// websocket (and its overhead) off the table while keeping all guide traffic on
// browser -> Knock, never touching our servers.
export function createGuidesStore() {
  const store = writable({
    guides: [],
    guideClient: null,
    isInitialized: false,
  });

  const { subscribe, update } = store;

  function refreshGuides(guideClient) {
    update((state) => ({
      ...state,
      guides: guideClient.selectGuides(guideClient.store.state),
    }));
  }

  return {
    subscribe,

    init: async ({ public_key, signed_key, user_id, guide_channel_id }) => {
      const state = get(store);

      if (state.isInitialized) {
        return;
      }

      if (!public_key || !signed_key || !guide_channel_id) {
        console.warn("Knock guides missing api keys or channel, disabling");
        return;
      }

      const knockClient = new Knock(public_key);
      knockClient.authenticate(user_id, signed_key);

      const guideClient = new KnockGuideClient(knockClient, guide_channel_id);

      // Keep the Svelte store in sync with the guide client's internal store so
      // selected guides recompute whenever location or eligibility changes.
      guideClient.store.subscribe(() => refreshGuides(guideClient));

      update((s) => ({ ...s, guideClient, isInitialized: true }));

      // Initial fetch of eligible guides. No `guideClient.subscribe()` — see the
      // note above; we refresh via `fetch()` on navigation instead.
      await guideClient.fetch();
      refreshGuides(guideClient);
    },

    // LiveView navigates via live_patch/live_redirect, which don't fire
    // `popstate`, so the SDK's window location tracking misses in-app
    // navigations. Push the new location and re-fetch to refresh eligibility.
    navigate: async (href) => {
      const { guideClient } = get(store);
      if (!guideClient) return;
      guideClient.setLocation(href);
      await guideClient.fetch();
      refreshGuides(guideClient);
    },

    cleanup: () => {
      const { guideClient } = get(store);
      guideClient?.cleanup();
    },

    // Engagement — operate on a guide's current step.
    markSeen: (guide) => guide?.getStep()?.markAsSeen(),
    markInteracted: (guide, metadata) => guide?.getStep()?.markAsInteracted({ metadata }),
    markArchived: (guide) => guide?.getStep()?.markAsArchived(),
  };
}
